import { alpha, Box, Dialog, darken, styled, Typography, useTheme } from '@mui/material';
import { type FC, useLayoutEffect, useRef, useState } from 'react';

interface IProps {
  textContent: string;
  heightSpan: number;
}

const OverflowingTextContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'heightSpan'
})<{ heightSpan: number }>(
  ({ theme, heightSpan }) => `
    &.long {
      overflow: hidden;
      height: ${heightSpan}px;
      position: relative;
    }

    &.long:after {
      content: '...  read more';
      top: ${heightSpan - 20}px;
      right: 0;
      position: absolute;
      color: ${theme.colors.avani.turc};
      background: ${theme.colors.avani.lightTurc};
    }
  `
);
export const OverflowingContent: FC<IProps> = ({ textContent, heightSpan }) => {
  const element = useRef<HTMLElement>(null);
  const frameElement = useRef<HTMLElement>(null);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [longContent, setLongContent] = useState(false);

  const isOverflowing = (el: HTMLElement | null) => {
    if (!el) return false;
    return el.clientHeight > heightSpan + 1;
  };

  useLayoutEffect(() => {
    const checkOverflow = () => {
      const isLong = isOverflowing(element.current);
      setLongContent(isLong);
      if (isLong) frameElement.current?.classList.add('long');
      else frameElement.current?.classList.remove('long');
    };

    // Initial check after DOM is laid out
    checkOverflow();

    // Re-check on resize (font changes, responsive layout, etc.)
    const ro = new ResizeObserver(() => checkOverflow());

    if (frameElement.current) ro.observe(frameElement.current);

    return () => ro.disconnect();
    //todo:v2-changes
  }, [textContent]);

  const theme = useTheme();
  return (
    <>
      <OverflowingTextContent
        ref={frameElement}
        heightSpan={heightSpan}
        onClick={() => longContent && setDialogOpen(true)}
        sx={{ [`:after`]: { fontFamily: theme.typography.body2 } }}
      >
        <Typography ref={element} fontSize={14} variant={'body2'} minHeight={'59px'}>
          {textContent ?? ''}
        </Typography>
      </OverflowingTextContent>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        keepMounted
        closeAfterTransition={false}
        slotProps={{
          backdrop: {
            style: { backgroundColor: alpha(darken('#000C57', 0.4), 0.2), backdropFilter: 'blur(2px)' }
          }
        }}
      >
        <Typography fontSize={18} variant={'body2'} sx={{ padding: 2 }}>
          {textContent ?? ''}
        </Typography>
      </Dialog>
    </>
  );
};
